import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@components/ui/Button/Button';
import { Field } from '@components/ui/Field/Field';
import { PhoneField } from '@components/ui/PhoneField/PhoneField';
import { MfaCodeInput } from '@components/Mfa/MfaCodeInput/MfaCodeInput';
import { FormActions } from './mfaPanels';
import {
  MfaInvalidPhoneError,
  MfaPasswordRejectedError,
  MfaRateLimitedError,
  sendMfaSmsOtp,
  verifyMfaSms,
} from '@apis/mfa';
import { MFA_CODE_LENGTH, MFA_OTP_RESEND_COOLDOWN_SECONDS } from '@constants/mfa';
import { useResendCooldown } from '@hooks/useResendCooldown';
import styles from './styles.module.css';

export interface ISmsEnrollmentProps {
  /**
   * Whether two-step verification is already ON. Decides whether the account password is part
   * of this enrolment: it is required only for the FIRST factor, because that is the call that
   * mints the backup codes.
   */
  twoFactorEnabled: boolean;
  /** Enrolment finished. `backupCodes` is present only when this was the first factor. */
  onEnrolled: (backupCodes?: string[]) => void;
  onCancel: () => void;
}

/** `password → phone → code`, or `phone → code` when 2FA is already on. */
type Step = 'password' | 'phone' | 'code';

/**
 * Enrolling SMS as a second factor, from the security page.
 *
 * Lives in its own component rather than as more branches of `SecurityPage`'s view machine for
 * two reasons: that file is close to the repo's 600-line ceiling, and this flow has a step the
 * email one does not — the number itself, which the user types and the backend proves before
 * storing. Keeping the two separate also keeps the email path, which is already in production,
 * untouched.
 *
 * The number is collected through the shared `PhoneField` — the same country-picker the admin
 * user dialog uses — so the country is CHOSEN rather than inferred. That matters beyond looking
 * consistent: the engine will not guess a country for a local number (guessing would text a
 * login code to a stranger abroad), and the picker is what lets a user satisfy that without ever
 * meeting the words "international format". The field reports E.164 or nothing, so an invalid
 * number cannot leave this screen.
 */
export function SmsEnrollment({ twoFactorEnabled, onEnrolled, onCancel }: ISmsEnrollmentProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>(twoFactorEnabled ? 'phone' : 'password');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { cooldown, startCooldown } = useResendCooldown();

  /** Text a code to the entered number. Returns whether it went out. */
  async function sendCode(): Promise<boolean> {
    if (sending || cooldown > 0) return false;
    setErr(null);
    setSending(true);
    try {
      await sendMfaSmsOtp(phone.trim());
      startCooldown(MFA_OTP_RESEND_COOLDOWN_SECONDS);
      return true;
    } catch (error) {
      // `PhoneField` only ever hands us a number it considers valid, so a format refusal from
      // the backend now means the two disagree — a real bug rather than a typo. Surface it as
      // the field message anyway; it is still the most actionable thing to show.
      setErr(
        error instanceof MfaInvalidPhoneError
          ? t('auth.mfa.settings.smsPhoneInvalid')
          : error instanceof MfaRateLimitedError
            ? t('auth.mfa.rateLimited')
            : t('auth.mfa.settings.smsSendError'),
      );
      return false;
    } finally {
      setSending(false);
    }
  }

  async function onSubmitPhone(e: FormEvent) {
    e.preventDefault();
    setCode('');
    if (await sendCode()) setStep('code');
  }

  async function submitCode(rawCode: string) {
    const value = rawCode.trim();
    if (value.length !== MFA_CODE_LENGTH || submitting) return;
    setErr(null);
    setSubmitting(true);
    try {
      const { backupCodes } = await verifyMfaSms(value, twoFactorEnabled ? undefined : password);
      setPassword('');
      setCode('');
      onEnrolled(backupCodes);
    } catch (error) {
      // One neutral failure for a wrong/expired/exhausted code AND a rejected password — the
      // backend does not distinguish them, so the copy names both fields where a password was
      // in play rather than misattributing a bad code to the password.
      setErr(
        error instanceof MfaRateLimitedError
          ? t('auth.mfa.rateLimited')
          : error instanceof MfaPasswordRejectedError && !twoFactorEnabled
            ? t('auth.mfa.settings.smsCodeOrPasswordError')
            : t('auth.mfa.settings.smsCodeError'),
      );
      setCode('');
    } finally {
      setSubmitting(false);
    }
  }

  if (step === 'password') {
    return (
      <form
        className={styles.subForm}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (password.length > 0) setStep('phone');
        }}
      >
        <div>
          <h3 className={styles.subTitle}>{t('auth.mfa.settings.smsPasswordTitle')}</h3>
          <p className={styles.subBody}>{t('auth.mfa.settings.smsPasswordBody')}</p>
        </div>
        <Field
          label={t('auth.mfa.settings.passwordLabel')}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          hint={t('auth.mfa.enroll.passwordHint')}
          autoComplete="current-password"
          autoFocus
          required
        />
        <div className={styles.formActions}>
          <Button variant="ghost" size="md" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" size="md" disabled={password.length === 0}>
            {t('common.continue')}
          </Button>
        </div>
      </form>
    );
  }

  if (step === 'phone') {
    return (
      <form className={styles.subForm} noValidate onSubmit={onSubmitPhone}>
        <div>
          <h3 className={styles.subTitle}>{t('auth.mfa.settings.smsPhoneTitle')}</h3>
          <p className={styles.subBody}>{t('auth.mfa.settings.smsPhoneBody')}</p>
        </div>
        {/*
          The same country-picker field the admin user dialog uses, so a phone number looks and
          behaves the same everywhere in the product. It reports E.164 only for a number valid in
          the chosen country and an empty string otherwise — which is precisely what the endpoint
          requires, so the backend's "international format" refusal is now unreachable from here
          and the user never has to know what E.164 is.
        */}
        <PhoneField
          label={t('auth.mfa.settings.smsPhoneLabel')}
          value={phone}
          onChange={setPhone}
          invalidMessage={t('auth.mfa.settings.smsPhoneInvalid')}
        />
        <p className={styles.subBody}>{t('auth.mfa.settings.smsPhoneHint')}</p>
        {err ? (
          <div className={styles.error} role="alert">
            {err}
          </div>
        ) : null}
        <div className={styles.formActions}>
          <Button variant="ghost" size="md" onClick={onCancel} disabled={sending}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" size="md" disabled={sending || phone.trim().length === 0}>
            {sending ? t('auth.mfa.settings.emailSending') : t('auth.mfa.settings.emailSendCode')}
          </Button>
        </div>
      </form>
    );
  }

  return (
    <form
      className={styles.subForm}
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void submitCode(code);
      }}
    >
      <div>
        <h3 className={styles.subTitle}>{t('auth.mfa.settings.smsCodeTitle')}</h3>
        <p className={styles.subBody}>{t('auth.mfa.settings.smsCodeBody', { phone: phone.trim() })}</p>
      </div>
      <MfaCodeInput
        label={t('auth.mfa.challenge.codeLabel')}
        value={code}
        onChange={setCode}
        onComplete={(full) => void submitCode(full)}
        disabled={submitting}
        autoFocus
      />
      <div className={styles.otpRow}>
        <Button variant="secondary" size="sm" onClick={() => void sendCode()} disabled={sending || cooldown > 0}>
          {cooldown > 0
            ? t('auth.mfa.settings.emailResendCooldown', { seconds: cooldown })
            : t('auth.mfa.settings.emailResend')}
        </Button>
        <span className={styles.sentNote} role="status">
          {t('auth.mfa.settings.smsOtpSent')}
        </span>
      </div>
      {err ? (
        <div className={styles.error} role="alert">
          {err}
        </div>
      ) : null}
      <FormActions
        onCancel={onCancel}
        busy={submitting}
        submitDisabled={submitting || code.length !== MFA_CODE_LENGTH}
        submitLabel={
          submitting ? t('auth.mfa.settings.emailConfirming') : t('auth.mfa.settings.emailConfirm')
        }
      />
    </form>
  );
}
